import {
  datadogRum,
  DefaultPrivacyLevel,
  RumEvent,
  RumEventDomainContext,
  RumFetchResourceEventDomainContext,
} from '@datadog/browser-rum-slim'

import { redactTokensRecursive } from '@cash-web/shared/util-redaction'
import { canTrack, Region } from '@cash-web/shared-util-can-track'
import { getEnvironmentForObservabilityTools, isServerEnvironment } from '@cash-web/shared-util-environment'

declare const RUM_APPLICATION_ID: string
declare const RUM_CLIENT_TOKEN: string
declare const RUM_SITE: string
declare const RUM_SERVICE: string
declare const RUM_APP_VERSION: string
declare const RUM_SAMPLE_RATE: number

const hasApiKey = () => {
  try {
    return !!RUM_APPLICATION_ID
  } catch {
    return false
  }
}

/**
 * See: @datadog/browser-rum-slim/node_modules/@datadog/browser-core/cjs/tools/matchOption.d.ts
 */
export type MatchOption = string | RegExp | ((value: string) => boolean)

export enum TrackingConsent {
  GRANTED = 'granted',
  NOT_GRANTED = 'not-granted',
}

export const initializeRum = (
  sessionSampleRateOverride: number,
  beforeSend?: (event: RumEvent, context: RumEventDomainContext) => boolean,
  trackingConsent?: TrackingConsent,
  excludedActivityUrls?: MatchOption[]
) => {
  const region = window?.countryGuess
  // Skip initializing in some situations
  if (isServerEnvironment() || !hasApiKey() || !canTrack(region as Region)) {
    return
  }

  const sessionSampleRate = sessionSampleRateOverride !== undefined ? sessionSampleRateOverride : RUM_SAMPLE_RATE || 0
  // Disable telemetry if session sampling is disabled to deflake e2e tests, otherwise
  // just use the default rate by passing undefined.
  const telemetrySampleRate = sessionSampleRate === 0 ? 0 : undefined

  try {
    datadogRum.init({
      applicationId: RUM_APPLICATION_ID,
      clientToken: RUM_CLIENT_TOKEN,

      site: RUM_SITE,
      service: RUM_SERVICE,
      env: getEnvironmentForObservabilityTools(),

      version: RUM_APP_VERSION,

      // If you pass a sessionSampleRate, which can be set dynamically in launchdarkly, it will
      // override the RUM_SAMPLE_RATE that is set in your webpack plugin configuration
      sessionSampleRate,
      telemetrySampleRate,

      // Disabling session replay for now, may experiment with turning it on in the future.
      // NOTE: if you enable session replay, you'll need to switch from the @datadog/browser-rum-slim package back to @datadog/browser-rum
      sessionReplaySampleRate: 0,

      trackResources: true,
      trackLongTasks: true,
      // Datadog Docs say this may cause logging of sensitive/private data. If we want to send
      // user interactions we likely need to manually send them with the addAction API since
      // we can't risk dumping PII into datadog.
      trackUserInteractions: false,
      allowedTracingUrls: [
        url => url.startsWith('https://cash.app'),
        url => url.startsWith('https://cashstaging.app'),
        // eslint-disable-next-line @nx/workspace/no-hardcoded-cdn-urls
        url => url.startsWith('https://cash-f.squarecdn.com'),
      ],

      excludedActivityUrls,

      // Mask all text and data attriburtes in the app by default, we can opt into unmasking
      // using data attributes if we feel confident a specific element will not leak PII.
      // (e.g. we have complete control of it, it's not sent by the server, etc.)
      defaultPrivacyLevel: DefaultPrivacyLevel.MASK,

      enableExperimentalFeatures: ['feature_flags'],

      trackingConsent,

      beforeSend(event: RumEvent, context) {
        redactTokensRecursive(event as Partial<RumEvent>)

        // Correlate RUM requests with server logs via X-Speleo-Trace-Id
        if (event.context && event.type === 'resource' && event.resource.type === 'fetch') {
          const fetchContext = context as RumFetchResourceEventDomainContext
          const fetchRequestHeaders = fetchContext.requestInit?.headers
          let traceId = ''

          if (fetchRequestHeaders && typeof fetchRequestHeaders === 'object') {
            if (fetchRequestHeaders instanceof Headers) {
              // fetchRequestHeaders are native fetch Headers
              traceId = fetchRequestHeaders.get('X-Speleo-Trace-Id') ?? ''
            } else if (Array.isArray(fetchRequestHeaders)) {
              // fetchRequestHeaders are [string, string][]`
              traceId = Object.fromEntries(fetchRequestHeaders as [string, string][])['X-Speleo-Trace-Id'] ?? ''
            } else {
              // fetchRequestHeaders are Record<string, string>
              traceId = (fetchRequestHeaders as Record<string, string>)['X-Speleo-Trace-Id'] ?? ''
            }
          }

          event.context['X-Speleo-Trace-ID'] = traceId ?? ''
        }

        if (beforeSend) {
          return beforeSend(event, context)
        }

        return true
      },
    })
  } catch (e) {
    /* eslint-disable no-console */
    console.error('Error initializing datadog RUM', e)
  }
}

/**
 * Explicitly stop the RUM session. This flushes the session and all of its events to DataDog and marks the session as ended.
 */
export const stopRumSession = () => {
  datadogRum.stopSession()
}
